import React, { useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import UtilContext from '../../components/UtilContext';
import { isItemVisible, itemAllowedForChildren } from './menuSearch';
import { openMenuSearchItem } from './maskOpen';

/** Posunie scroll kontajnera tak, aby bola celá rozbalená položka viditeľná (reaguje na aktuálnu výšku okna). */
function scrollMenuItemIntoView(scrollContainer, element) {
  if (!scrollContainer || !element) return;

  const padding = 12;
  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const containerHeight = scrollContainer.clientHeight;

  const relativeTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
  const relativeBottom = relativeTop + elementRect.height;

  const visibleTop = scrollContainer.scrollTop + padding;
  const visibleBottom = scrollContainer.scrollTop + containerHeight - padding;

  if (relativeBottom > visibleBottom) {
    scrollContainer.scrollTop = relativeBottom - containerHeight + padding;
  }
  if (relativeTop < scrollContainer.scrollTop + padding) {
    scrollContainer.scrollTop = relativeTop - padding;
  }
}

function menuLeafLabel(item, t) {
  if (item?._searchLabel) return item._searchLabel;
  if (item?._maskSearchHit && item?._maskDisplayCode) {
    return `${item._maskDisplayCode} — ${t(item.title || item.page)}`;
  }
  return t(item.title || item.page);
}

function Menu({ items, depth, searchMode, displayChildren, setDisplayChildren, menuOpen, setMenuOpen, menuScrollRef, parentAllowed = true, parentTitle }) {
  const [, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const { toast, setVisible, setDialogText } = useContext(UtilContext);
  const prevDisplayChildrenRef = useRef(displayChildren);

  const visibleItems = useMemo(
    () => (items || []).filter((item) => isItemVisible(item, user?.permissions, parentAllowed)),
    [items, user?.permissions, parentAllowed]
  );

  useLayoutEffect(() => {
    if (depth !== 1 || menuOpen || !menuScrollRef?.current) return;

    const scrollContainer = menuScrollRef.current;
    const prev = prevDisplayChildrenRef.current;

    Object.entries(displayChildren).forEach(([id, isExpanded]) => {
      if (isExpanded && !prev[id]) {
        const el = scrollContainer.querySelector(`[data-menu-item-id="${id}"]`);
        if (el) scrollMenuItemIntoView(scrollContainer, el);
      }
    });

    prevDisplayChildrenRef.current = displayChildren;
  }, [displayChildren, depth, menuOpen, menuScrollRef]);

  return (
    <ul
      key={locale}
      className={`${
        depth === 3 || depth == 4 ? 'bg-[#333] ' : ' '
      } rounded-sm pt-2 pb-2 space-y-1 text-sm`}
    >
      {visibleItems.map((item) => (
        <React.Fragment key={item.id}>
          {item.children ? (
            <li className="rounded-sm " data-menu-item-id={item.id}>
              {item.children && (
                <button
                  type="button"
                  className={`${
                    depth === 2 ? 'pl-6 ' : ' '
                  } ${
                    depth === 3 ? 'pl-9  ' : ' '
                  } ${
                    depth === 4 ? 'pl-12 ' : ' '
                  } flex items-center w-full p-2 space-x-3  rounded-md group text-white hover:bg-[#555]`}
                  onClick={() => {
                    setDisplayChildren({
                      ...displayChildren,
                      [item.id]: !displayChildren[item.id],
                    });
                    if (menuOpen) setMenuOpen(false);
                  }}
                >
                  {depth === 1 && item.icon}
                  {!menuOpen && (
                    <>
                      <span className="text-gray-100 flex-1 text-left whitespace-nowrap">
                        {t(item.title || item.page)}
                      </span>
                      {!displayChildren[item.id] ? (
                        <i className="pi pi-angle-down"></i>
                      ) : (
                        <i className="pi pi-angle-up"></i>
                      )}
                    </>
                  )}
                </button>
              )}
              {displayChildren[item.id] && item.children && (
                <Menu
                  items={item.children}
                  depth={depth + 1}
                  searchMode={searchMode}
                  displayChildren={displayChildren}
                  setDisplayChildren={setDisplayChildren}
                  menuOpen={menuOpen}
                  setMenuOpen={setMenuOpen}
                  menuScrollRef={menuScrollRef}
                  parentAllowed={itemAllowedForChildren(item, user?.permissions, parentAllowed)}
                  parentTitle={item.title || item.page || parentTitle}
                />
              )}
            </li>
          ) : (
            <li className={`${depth === 3 ? 'bg-[#333] ' : ' '}`}>
              <button
                type="button"
                onClick={() => {
                  openMenuSearchItem({
                    item,
                    parentTitle,
                    userPermissions: user?.permissions,
                    dispatch,
                    t,
                    toast,
                    setVisible,
                    setDialogText,
                  });
                }}
                className={`${
                  depth === 2 ? 'pl-6 ' : ' '
                } ${
                  depth === 3 ? 'pl-9  ' : ' '
                } ${
                  depth === 4 ? 'pl-12 ' : ' '
                } flex items-center w-full p-2 space-x-3  text-white transition duration-75 rounded-lg group hover:bg-[#555]`}
              >
                {!menuOpen && (
                  <span className="text-left whitespace-nowrap overflow-hidden text-ellipsis">
                    {menuLeafLabel(item, t)}
                  </span>
                )}
              </button>
            </li>
          )}
        </React.Fragment>
      ))}
    </ul>
  );
}

export default Menu;
